// AYA PRO — TAXES & MAIRIE : jauge de conformité, avis journaliers, paiement simulé, reçus QR.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ProShell from "@/pro/ProShell";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";
const fmtF = (m) => `${new Intl.NumberFormat("fr-FR").format(m || 0)} FCFA`;
const newIK = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

async function taxApi(path, { method = "GET", body, idempotency } = {}) {
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
  if (idempotency) headers["Idempotency-Key"] = idempotency;
  const r = await fetch(`${API}/api/v3/taxes${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d.ok === false) { const e = new Error(d.message || "Erreur"); e.code = d.error; throw e; }
  return d;
}

const METHODS = [
  { id: "wallet", icon: "💛", label: "Wallet AYA" },
  { id: "orange", icon: "🟠", label: "Orange Money" },
  { id: "mtn", icon: "🟡", label: "MTN MoMo" },
  { id: "wave", icon: "🔵", label: "Wave" },
];

function SetupForm({ communes, onCreated }) {
  const [form, setForm] = useState({ commune: "", merchant_name: "", trade_type: "", daily_rate_minor: "500" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      await taxApi("/account", { method: "POST", body: { ...form, daily_rate_minor: Math.round(Number(form.daily_rate_minor)) } });
      toast.success("Compte redevance créé ✓");
      onCreated();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="pro-teaser" data-testid="tax-setup-form">
      <h2>🏛️ Enregistrer mon commerce</h2>
      <p>Recevez chaque jour votre avis de redevance et payez directement dans AYA PRO. Reçu officiel avec QR code vérifiable par votre mairie.</p>
      <div style={{ display: "grid", gap: 10 }}>
        <select className="pro-chat-input" data-testid="tax-commune-select" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })}>
          <option value="">— Choisir ma commune —</option>
          {communes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="pro-chat-input" data-testid="tax-name-input" placeholder="Nom du commerçant / de la boutique" value={form.merchant_name} onChange={(e) => setForm({ ...form, merchant_name: e.target.value })} />
        <input className="pro-chat-input" data-testid="tax-trade-input" placeholder="Type de commerce (ex : étal de vivriers)" value={form.trade_type} onChange={(e) => setForm({ ...form, trade_type: e.target.value })} />
        <input className="pro-chat-input" data-testid="tax-rate-input" inputMode="numeric" placeholder="Redevance journalière (FCFA)" value={form.daily_rate_minor} onChange={(e) => setForm({ ...form, daily_rate_minor: e.target.value })} />
        <button className="pro-btn pro-btn-gold" style={{ padding: "13px" }} data-testid="tax-setup-submit-btn" disabled={busy || !form.commune || !form.merchant_name.trim()} onClick={create}>
          {busy ? "…" : "Activer mes avis journaliers ✓"}
        </button>
      </div>
      <p style={{ fontSize: 11, color: "#9C9078", marginTop: 10 }}>Pilote : le tarif réel sera fixé par votre mairie lors du partenariat officiel.</p>
    </div>
  );
}

export default function ProTaxesPage() {
  const navigate = useNavigate();
  const [communes, setCommunes] = useState([]);
  const [account, setAccount] = useState(undefined);
  const [notices, setNotices] = useState([]);
  const [dueTotal, setDueTotal] = useState(0);
  const [dueCount, setDueCount] = useState(0);
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("wallet");
  const [busy, setBusy] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cm, acc] = await Promise.all([taxApi("/communes"), taxApi("/account")]);
      setCommunes(cm.communes);
      setAccount(acc.account);
      if (acc.account) {
        const [n, p] = await Promise.all([taxApi("/notices"), taxApi("/payments")]);
        setNotices(n.notices);
        setDueTotal(n.due_total_minor);
        setDueCount(n.due_count);
        setPayments(p.payments);
      }
    } catch { setAccount(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await taxApi("/pay", { method: "POST", idempotency: newIK(), body: { method, confirm: true, base: window.location.origin } });
      setLastReceipt(r.payment);
      setPayOpen(false);
      toast.success(`Reçu ${r.payment.receipt_number} généré ✓`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const receiptPdf = (p) => `${API}/api/v3/taxes/receipt/${p.payment_id}/pdf?t=${localStorage.getItem(TOKEN_KEY)}`;
  const upToDate = account && dueCount === 0;

  return (
    <ProShell>
      <button className="pro-back" data-testid="tax-back-btn" onClick={() => navigate("/pro")}>← Retour à l'accueil PRO</button>
      <div className="pro-hero">
        <h1 data-testid="tax-page-title">🏛️ Taxes & <span>Mairie</span></h1>
        <p>{account ? `${account.merchant_name} — Mairie de ${account.commune} · ${fmtF(account.daily_rate_minor)}/jour` : "Redevances journalières des 13 communes d'Abidjan"}</p>
      </div>

      {account === undefined && <div className="pro-empty">Chargement…</div>}
      {account === null && <SetupForm communes={communes} onCreated={load} />}

      {account && (
        <>
          <div className="pro-teaser" style={{ borderColor: upToDate ? "rgba(30,142,74,.5)" : "rgba(212,80,60,.5)" }} data-testid="tax-status-card">
            <h2>{upToDate ? "✅ Vous êtes à jour !" : `⏰ ${dueCount} jour(s) à régler`}</h2>
            <p>{upToDate
              ? "Toutes vos redevances journalières sont payées. La mairie peut vérifier vos reçus par QR code."
              : `Montant cumulé dû : ${fmtF(dueTotal)}. Réglez en une fois via le wallet ou mobile money (simulation pilote).`}</p>
            {!upToDate && (
              <button className="pro-btn pro-btn-gold" style={{ padding: 13 }} data-testid="tax-pay-btn" onClick={() => setPayOpen(true)}>
                💳 Payer {fmtF(dueTotal)}
              </button>
            )}
          </div>

          {lastReceipt && (
            <div className="pro-teaser" data-testid="tax-last-receipt">
              <h2>🧾 Reçu {lastReceipt.receipt_number}</h2>
              <p>{fmtF(lastReceipt.amount_minor)} — {lastReceipt.days_covered.length} jour(s) réglé(s). Présentez le QR code du PDF à l'agent municipal.</p>
              <a className="pro-btn pro-btn-gold" style={{ padding: 13, textDecoration: "none" }} data-testid="tax-last-receipt-pdf" href={receiptPdf(lastReceipt)} target="_blank" rel="noreferrer">📄 Ouvrir le reçu PDF</a>
            </div>
          )}

          <div className="pro-section-title">Avis journaliers</div>
          <div className="pro-listing" data-testid="tax-notices-list">
            {notices.slice(0, 14).map((n) => (
              <div key={n.notice_id} className="pro-partner-card" style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{new Date(`${n.day}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                  <div style={{ fontSize: 12, color: "#9C9078" }}>{fmtF(n.amount_minor)}</div>
                </div>
                <span className="pro-partner-badge" style={n.status === "PAID" ? { background: "rgba(30,142,74,.18)", color: "#7FD1AC", borderColor: "rgba(30,142,74,.4)" } : { background: "rgba(212,80,60,.15)", color: "#E8A196", borderColor: "rgba(212,80,60,.4)" }}>
                  {n.status === "PAID" ? "Payé ✓" : "À payer"}
                </span>
              </div>
            ))}
          </div>

          {payments.length > 0 && (
            <>
              <div className="pro-section-title">Mes reçus</div>
              <div className="pro-listing" data-testid="tax-payments-list">
                {payments.map((p) => (
                  <div key={p.payment_id} className="pro-partner-card" style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.receipt_number}</div>
                      <div style={{ fontSize: 12, color: "#9C9078" }}>{fmtF(p.amount_minor)} · {p.days_covered.length} jour(s) · {new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
                    </div>
                    <a className="pro-btn pro-btn-outline" style={{ flex: "none", padding: "8px 12px", fontSize: 12.5, textDecoration: "none" }} href={receiptPdf(p)} target="_blank" rel="noreferrer" data-testid={`tax-receipt-pdf-${p.payment_id}`}>📄 PDF</a>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {payOpen && (
        <div className="desk-modal-overlay" style={{ zIndex: 200 }} onClick={() => !busy && setPayOpen(false)}>
          <div style={{ width: "100%", maxWidth: 440, background: "#17130C", border: "1px solid rgba(212,175,55,.4)", borderRadius: 22, padding: 20 }} onClick={(e) => e.stopPropagation()} data-testid="tax-pay-modal">
            <h3 style={{ margin: "0 0 4px", color: "#F2EAD8" }}>Payer {fmtF(dueTotal)}</h3>
            <p style={{ fontSize: 13, color: "#9C9078", margin: "0 0 12px" }}>{dueCount} jour(s) de redevance — Mairie de {account?.commune}. Paiement SIMULÉ (pilote).</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {METHODS.map((m) => (
                <button key={m.id} className={`pro-cat-card ${method === m.id ? "" : ""}`} style={{ cursor: "pointer", alignItems: "center", borderColor: method === m.id ? "#D4AF37" : undefined, background: method === m.id ? "rgba(212,175,55,.15)" : undefined }} data-testid={`tax-method-${m.id}`} onClick={() => setMethod(m.id)}>
                  <span style={{ fontSize: 22 }}>{m.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#F2EAD8" }}>{m.label}</span>
                </button>
              ))}
            </div>
            <button className="pro-btn pro-btn-gold" style={{ width: "100%", padding: 13, marginTop: 14 }} data-testid="tax-pay-confirm-btn" disabled={busy} onClick={pay}>
              {busy ? "Paiement…" : `Confirmer le paiement ✓`}
            </button>
            <button className="pro-btn pro-btn-outline" style={{ width: "100%", padding: 12, marginTop: 8 }} disabled={busy} onClick={() => setPayOpen(false)}>Annuler</button>
          </div>
        </div>
      )}
    </ProShell>
  );
}
